/*
 * FUNanc1al metered registration wall
 *
 * Default policy:
 * - Three distinct articles per rolling 30-day period
 * - The fourth article is gated at 30% reading progress
 * - Authenticated readers remain unrestricted
 * - Authentication outages fail open
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'funanc1al_article_meter_v1';
  var SCROLL_KEY_PREFIX = 'funanc1al_return_scroll:';

  function initializeFunWall() {
    var wall = document.querySelector('[data-fun-wall]');

    if (!wall) {
      return;
    }

    /*
     * Never interfere with Shopify's visual theme editor.
     * Use the theme's external Preview link for testing.
     */
    if (window.Shopify && window.Shopify.designMode) {
      return;
    }

    /*
     * Explicit crawler protection. Most crawlers will not execute this
     * interaction anyway, but recognized bots are excluded deliberately.
     */
    var userAgent = String(navigator.userAgent || '');

    if (
      /bot|crawler|spider|slurp|bingpreview|facebookexternalhit|linkedinbot|twitterbot|pinterest|lighthouse/i.test(
        userAgent
      )
    ) {
      return;
    }

    var articleContent = document.querySelector('.post .text.rte');

    if (!articleContent) {
      return;
    }

    var statusUrl = wall.getAttribute('data-status-url') || '';
    var articleUrl =
      wall.getAttribute('data-article-url') || window.location.href;
    var articleKey =
      wall.getAttribute('data-article-key') || window.location.pathname;

    var scrollTrigger =
      parseInt(wall.getAttribute('data-scroll-trigger'), 10) || 30;
    var freeArticles =
      parseInt(wall.getAttribute('data-free-articles'), 10) || 3;
    var meterDays =
      parseInt(wall.getAttribute('data-meter-days'), 10) || 30;

    var query = new URLSearchParams(window.location.search);
    var forceTest = query.get('funwall_test') === '1';
    var showImmediately = query.get('funwall_now') === '1';
    var resetMeter = query.get('funwall_reset') === '1';

    var wallShown = false;
    var readingRecorded = false;

    if (resetMeter) {
      removeStorage(STORAGE_KEY);
      removeSessionStorage(SCROLL_KEY_PREFIX + articleUrl);
    }

    restoreSavedScroll(articleUrl);

    /*
     * Verify the first-party FUNanc1al authentication cookie before
     * evaluating the anonymous article meter.
     */
    checkAuthentication(statusUrl)
      .then(function (authenticated) {
        if (authenticated) {
          removeStorage(STORAGE_KEY);
          restoreSavedScroll(articleUrl);
          return;
        }

        activateAnonymousMeter();
      })
      .catch(function () {
        /*
         * Fail open. A network, CORS, or authentication-service outage
         * must never incorrectly lock out a legitimate reader.
         */
        console.warn(
          'FUNanc1al registration status could not be verified.'
        );
      });

    function activateAnonymousMeter() {
      function evaluateReadingProgress() {
        if (wallShown) {
          return;
        }

        var bounds = articleContent.getBoundingClientRect();
        var articleTop = bounds.top + window.pageYOffset;
        var articleHeight = Math.max(
          articleContent.scrollHeight,
          bounds.height,
          1
        );
        var viewportBottom =
          window.pageYOffset + window.innerHeight;

        var progress =
          ((viewportBottom - articleTop) / articleHeight) * 100;

        if (showImmediately || progress >= scrollTrigger) {
          window.removeEventListener(
            'scroll',
            evaluateReadingProgress
          );
          window.removeEventListener(
            'resize',
            evaluateReadingProgress
          );

          if (forceTest || showImmediately) {
            showWall();
            return;
          }

          if (!readingRecorded) {
            readingRecorded = true;

            var meter = recordArticleRead(
              articleKey,
              meterDays
            );

            /*
             * Three articles remain completely free.
             * The fourth distinct article activates the wall.
             */
            if (meter.length > freeArticles) {
              showWall();
            }
          }
        }
      }

      window.addEventListener(
        'scroll',
        evaluateReadingProgress,
        { passive: true }
      );

      window.addEventListener(
        'resize',
        evaluateReadingProgress,
        { passive: true }
      );

      evaluateReadingProgress();
    }

    function showWall() {
      if (wallShown) {
        return;
      }

      wallShown = true;
      wall.hidden = false;

      document.documentElement.classList.add('fun-wall-open');
      document.body.classList.add('fun-wall-open');

      var dialog = wall.querySelector('.fun-wall__dialog');
      var loginLink = wall.querySelector(
        '[data-fun-wall-login]'
      );

      if (loginLink) {
        loginLink.addEventListener('click', function () {
          saveCurrentScroll(articleUrl);
        });
      }

      if (dialog) {
        window.requestAnimationFrame(function () {
          dialog.focus();
        });

        dialog.addEventListener('keydown', trapFocus);
      }
    }

    function trapFocus(event) {
      if (event.key !== 'Tab') {
        return;
      }

      var focusable = wall.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      if (!focusable.length) {
        event.preventDefault();
        return;
      }

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === last
      ) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  function checkAuthentication(statusUrl) {
    if (!statusUrl || typeof window.fetch !== 'function') {
      return Promise.reject(
        new Error('Authentication status unavailable.')
      );
    }

    return window
      .fetch(statusUrl, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        cache: 'no-store',
        headers: {
          Accept: 'application/json'
        }
      })
      .then(function (response) {
        if (!response.ok) {
          throw new Error(
            'Authentication status request failed.'
          );
        }

        return response.json();
      })
      .then(function (payload) {
        return payload && payload.authenticated === true;
      });
  }

  function recordArticleRead(articleKey, meterDays) {
    var now = Date.now();
    var cutoff =
      now - meterDays * 24 * 60 * 60 * 1000;

    var meter = readMeter().filter(function (entry) {
      return (
        entry &&
        typeof entry.key === 'string' &&
        typeof entry.seenAt === 'number' &&
        entry.seenAt >= cutoff
      );
    });

    var alreadyRecorded = meter.some(function (entry) {
      return entry.key === String(articleKey);
    });

    if (!alreadyRecorded) {
      meter.push({
        key: String(articleKey),
        seenAt: now
      });
    }

    writeMeter(meter);

    return meter;
  }

  function readMeter() {
    try {
      var stored = window.localStorage.getItem(STORAGE_KEY);
      var parsed = stored ? JSON.parse(stored) : [];

      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function writeMeter(meter) {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(meter)
      );
    } catch (error) {
      /*
       * If storage is unavailable, remain permissive rather than
       * creating an unreliable wall.
       */
    }
  }

  function removeStorage(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      /* No action required. */
    }
  }

  function saveCurrentScroll(articleUrl) {
    try {
      window.sessionStorage.setItem(
        SCROLL_KEY_PREFIX + articleUrl,
        String(Math.max(0, Math.round(window.pageYOffset)))
      );
    } catch (error) {
      /* Scroll restoration remains optional. */
    }
  }

  function restoreSavedScroll(articleUrl) {
    try {
      var key = SCROLL_KEY_PREFIX + articleUrl;
      var stored = window.sessionStorage.getItem(key);

      if (stored === null) {
        return;
      }

      var position = parseInt(stored, 10);

      window.sessionStorage.removeItem(key);

      if (!Number.isFinite(position) || position < 0) {
        return;
      }

      window.setTimeout(function () {
        window.scrollTo({
          top: position,
          left: 0,
          behavior: 'auto'
        });
      }, 150);
    } catch (error) {
      /* Scroll restoration remains optional. */
    }
  }

  function removeSessionStorage(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      /* No action required. */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeFunWall,
      { once: true }
    );
  } else {
    initializeFunWall();
  }
})();